import ArrowBack from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { useDesignSystemEnabled } from '@openx/components-core/lib/theme/useDesignSystemEnabled.ts';
import { getInstancePrefixedAppName } from '@openx/components-core/utils/getInstancePrefixedAppName.ts';
import { type JSX, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StyledBackButton,
  StyledContentContainerDiv,
  StyledDivider,
  StyledHeaderContainerDiv,
  StyledSubtitleTypography,
  StyledTitleBadgeTypography,
  StyledTitleContainerGrid,
  StyledTitleTypography,
} from './PageHeader.styled.tsx';
import { PageHeaderDesignSystem } from './pageHeaderDesignSystem.tsx';

export type TitlePrefix = 'DSP UI' | 'OpenXSelect' | 'OXControl' | 'Search' | 'TraceX' | 'Unity';

export interface PageHeaderProps {
  children?: ReactNode;
  title?: string | undefined;
  titleBadge?: string | ReactNode | undefined;
  render?: () => ReactNode;
  titlePrefix?: TitlePrefix;
  goBack?: () => void;
  backButtonTestId?: string;
  customGoBackText?: string;
  emptyMode?: boolean;
  emptyModeRender?: () => ReactNode;
  PreTitleIconComponent?: JSX.Element;
  sticky?: boolean;
  /** Second line under the title; rendered by both the current and the design-system (`designSystem`) variants. */
  subtitle?: ReactNode;
  /**
   * Explicit override of the design-system variant (title row of the design-system `AppShell` template). When
   * omitted, the `ui-design-system` GrowthBook feature decides (`pageHeader` in its list); without a
   * GrowthBookProvider the current header renders. Same contract as `designSystem` on `AutoComplete`.
   *
   * The design-system variant does not render `goBack` / `customGoBackText` / `backButtonTestId`,
   * `emptyMode` / `emptyModeRender`, `titleBadge` and `PreTitleIconComponent`: there is no automatic fallback to the
   * current header, so pages using them must not be switched (`designSystem={false}`) until UIG-1237 covers them.
   */
  designSystem?: boolean;
}

export function PageHeader({
  children,
  title,
  subtitle,
  titleBadge,
  titlePrefix,
  render,
  goBack,
  backButtonTestId = 'back-button',
  customGoBackText,
  emptyMode = false,
  emptyModeRender,
  PreTitleIconComponent,
  sticky = false,
  designSystem,
}: PageHeaderProps): JSX.Element {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  const isDesignSystemEnabled = useDesignSystemEnabled('pageHeader', designSystem);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <title mounts the node and changes its content without resizing it>
  useEffect(() => {
    const titleNode = titleRef.current;

    if (!titleNode || typeof ResizeObserver === 'undefined') {
      return;
    }

    const checkTruncation = () => setIsTitleTruncated(titleNode.scrollHeight > titleNode.clientHeight);
    const resizeObserver = new ResizeObserver(checkTruncation);

    checkTruncation();
    resizeObserver.observe(titleNode);

    return () => resizeObserver.disconnect();
  }, [title]);

  useEffect(() => {
    if (!title && !titlePrefix) {
      return;
    }

    document.title = `${getInstancePrefixedAppName(titlePrefix || 'Unity')}${title ? ` - ${title}` : ''}`;
  }, [title, titlePrefix]);

  const renderTitleBadge = useCallback(() => {
    if (typeof titleBadge === 'string') {
      return <StyledTitleBadgeTypography data-test="page-header-badge">{titleBadge}</StyledTitleBadgeTypography>;
    }

    return <Box sx={{ ml: 2 }}>{titleBadge}</Box>;
  }, [titleBadge]);

  // Design-system variant (pageHeaderDesignSystem.tsx). Placed after all hooks: an early return before them would
  // break the rules of hooks.
  if (isDesignSystemEnabled) {
    return (
      <PageHeaderDesignSystem title={title} subtitle={subtitle} render={render} sticky={sticky}>
        {children}
      </PageHeaderDesignSystem>
    );
  }

  return (
    <>
      <StyledHeaderContainerDiv data-test="page-header" sticky={sticky} hasSubtitle={!!subtitle}>
        {emptyMode ? (
          emptyModeRender?.()
        ) : (
          <Grid
            container
            wrap="nowrap"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              flexGrow: 1,
            }}
          >
            <StyledTitleContainerGrid
              container
              wrap="nowrap"
              sx={{ alignItems: 'center', ...(title && { flex: '1 1 0', pr: 2 }) }}
            >
              {goBack && (
                <>
                  <StyledBackButton
                    variant="text"
                    startIcon={<ArrowBack />}
                    color="inherit"
                    onClick={goBack}
                    data-test={backButtonTestId}
                  >
                    {customGoBackText ?? t('Back')}
                  </StyledBackButton>
                  <StyledDivider orientation="vertical" />
                </>
              )}

              {PreTitleIconComponent && PreTitleIconComponent}

              {title && (
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Box sx={{ display: 'flex', minWidth: 0 }}>
                    <Tooltip title={isTitleTruncated ? title : ''}>
                      <StyledTitleTypography ref={titleRef} variant="h1" data-test="page-header-title">
                        {title}
                      </StyledTitleTypography>
                    </Tooltip>
                    {titleBadge && renderTitleBadge()}
                  </Box>
                  {!!subtitle && (
                    <StyledSubtitleTypography variant="body2" data-test="page-header-subtitle">
                      {subtitle}
                    </StyledSubtitleTypography>
                  )}
                </Box>
              )}
            </StyledTitleContainerGrid>
            {render?.()}
          </Grid>
        )}
      </StyledHeaderContainerDiv>
      <StyledContentContainerDiv hasChildren={children}>{children}</StyledContentContainerDiv>
    </>
  );
}
